import { useCallback, useRef, type ComponentProps, type ChangeEvent, useImperativeHandle } from "react";
import { Button } from "@core/components/ui/Button";
import { CircleX, CircleCheck, CircleAlert } from "lucide-react";
import { cn } from "@core/utils/domUtil";

export interface InputProps extends ComponentProps<"input"> {
  /** InputProps: 값(상태값) */
  value?: string;
  /** InputProps: value(상태값) 변경 이벤트 */
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Input value(상태값) set함수 (useDelete: true일 때 필요) */
  setValue?: (value: string) => void;
  /** InputProps: 클래스명 */
  className?: string;
  /** InputProps: input type */
  type?: string;
  /** InputProps: 에러 메시지 */
  error?: { message: string };
  /** InputProps: 성공 메시지 */
  success?: { message: string };
  /** InputProps: 삭제 버튼 사용 여부 */
  useDelete?: boolean;
  /** InputProps: 읽기 전용 여부 */
  readonly?: boolean;
  /** InputProps: 비활성화 여부  */
  disabled?: boolean;
  /** InputProps: 플레이스홀더 텍스트 */
  placeholder?: string;
  /** FormInputProps: 입력 필드를 감싸는 div태그에 적용할 추가 클래스 */
  inputWrapClassName?: string;
}

function Input({ inputWrapClassName, className, type, error, success, useDelete = false, placeholder, value, onChange, setValue, ...props }: InputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(props.ref, () => inputRef.current as HTMLInputElement);

  const onClickDelete = useCallback(() => {
    setValue?.("");
    inputRef.current?.focus();
  }, [setValue]);

  return (
    <>
      <div className={cn("group relative", inputWrapClassName)}>
        <input type={type} data-slot="input" className={cn(`input-common input-default w-full`, useDelete && "delete", success && "success", error && "error border-error!", className)} {...{ ...props, value, placeholder, onChange }} ref={inputRef} />
        {useDelete && (
          <Button variant="text" size="small" className="input-icon hidden group-focus-within:block" onClick={onClickDelete}>
            <CircleX className="stroke-gray-800 stroke-2" />
          </Button>
        )}
        {success && <CircleCheck className="input-icon stroke-green" />}
        {error && <CircleAlert className="input-icon stroke-red" />}
      </div>
      {success && <p className="text-success mt-[7px] text-sm">{success.message}</p>}
      {error && <p className="text-error mt-[7px] text-sm">{error.message}</p>}
    </>
  );
}

export { Input };
